import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IconName, useHandleClickOutside, useLockBodyScroll } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { Box, Breakpoint, Menu, MenuIcon, Text } from '@yonderland/andes-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { MobileNavigationItemContent } from '../../../components/navigation/mobile-navigation/MobileNavigationItemContent';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { analyticsActions } from '../../../redux/analytics/actions/AnalyticsActions.creators';
import { MobileNavigationAccount } from './MobileNavigationAccount';
import { MobileNavigationSearch } from './MobileNavigationSearch';
import { NavigationBasketBadge } from './NavigationBasketBadge';
import { NavigationWishlistBadge } from './NavigationWishlistBadge';
const HEADER_HEIGHT = '64px';
const MENU_MAX_WIDTH = '525px';
const getMenuItem = ({ item, idPostfix, isShopLanding, parentLevelId, backBtnLabel, parentAnalyticsBannerId, }) => {
    var _a;
    if (item.type === 'wishlist' && isShopLanding) {
        return null;
    }
    return (_jsx(Menu.Item, { testId: `mobile-navigation-item-${idPostfix}`, levelId: ((_a = item.subItems) === null || _a === void 0 ? void 0 : _a.length) ? `${parentLevelId}/${item.id}` : undefined, subLevel: getMenuLevel({ item, backBtnLabel, parentLevelId, isShopLanding, parentAnalyticsBannerId }), spacing: item.spacing, href: item.href, children: _jsxs(MobileNavigationItemContent, { icon: item.icon, active: item.active, emphasized: item.emphasized, image: item.image, children: [item.label, item.type === 'basket' && _jsx(NavigationBasketBadge, {}), item.type === 'wishlist' && _jsx(NavigationWishlistBadge, {})] }) }, item.id));
};
const getMenuLevel = ({ item, backBtnLabel, parentLevelId, isRootLevel, isShopLanding, searchConfig, accountConfig, languageItem, currencyItem, parentAnalyticsBannerId, }) => {
    var _a, _b;
    const levelId = `${parentLevelId ? `${parentLevelId}/` : ''}${item.id}`;
    const analyticsBannerId = isRootLevel
        ? ''
        : `${parentAnalyticsBannerId ? `${parentAnalyticsBannerId}-` : ''}${item.title.replace(' ', '-').toLowerCase()}`;
    return ((_a = item === null || item === void 0 ? void 0 : item.subItems) === null || _a === void 0 ? void 0 : _a.length) ? (_jsxs(Menu.Level, { id: levelId, backBtnLabel: backBtnLabel, rootLevel: isRootLevel, testId: "mobile-navigation-level", analyticsBannerId: isRootLevel ? undefined : `header-navigation-flyout-${analyticsBannerId}`, children: [!isRootLevel && (_jsx(Menu.Item, { href: item.href, testId: "mobile-navigation-title", children: _jsx(MobileNavigationItemContent, { emphasized: true, children: item.title }) })), isRootLevel && (searchConfig === null || searchConfig === void 0 ? void 0 : searchConfig.enabled) && (_jsx(Menu.Item, { spacing: "l", verticalInnerSpacing: false, testId: "mobile-navigation-search", children: _jsx(MobileNavigationSearch, Object.assign({}, searchConfig)) })), (_b = item.subItems) === null || _b === void 0 ? void 0 : _b.map((subItem, index) => getMenuItem({
                isShopLanding,
                item: subItem,
                idPostfix: index,
                parentLevelId: levelId,
                backBtnLabel: item.title,
                parentAnalyticsBannerId: analyticsBannerId,
            })), isRootLevel && (accountConfig === null || accountConfig === void 0 ? void 0 : accountConfig.enabled) && (_jsx(MobileNavigationAccount, { testId: "mobile-navigation-item-account", spacing: languageItem || currencyItem ? 'l' : 's', parentLevelId: levelId, backBtnLabel: item.title, login: accountConfig.login, registration: accountConfig.registration, accountMenu: accountConfig.accountMenu })), isRootLevel &&
                languageItem &&
                getMenuItem({
                    item: languageItem,
                    idPostfix: 'language',
                    parentLevelId: levelId,
                    backBtnLabel: item.title,
                }), isRootLevel &&
                currencyItem &&
                getMenuItem({
                    item: currencyItem,
                    idPostfix: 'currency',
                    parentLevelId: levelId,
                    backBtnLabel: item.title,
                })] }, item.id)) : null;
};
export const MobileNavigation = ({ menuItems, defaultLevel, isABTestEnabled, searchConfig, accountConfig, currencies, }) => {
    var _a;
    const { t } = useTranslation([I18N_NAMESPACES.general, I18N_NAMESPACES.navigation]);
    const { isShopLanding, alternateUrl, lang } = useAemConfig();
    const languages = Object.keys(alternateUrl);
    const [isOpen, setIsOpen] = useState(undefined); // undefined to avoid playing animation on init
    const [shouldRenderMenu, setShouldRenderMenu] = useState(false);
    const [isMenuClicked, setIsMenuClicked] = useState(false);
    const menuWrapperRef = useHandleClickOutside(Boolean(isOpen), setIsOpen);
    const dispatch = useDispatch();
    const handleTriggerClick = (e) => {
        e.stopPropagation(); // to avoid click outside triggering
        setIsOpen(prev => !prev);
        !isOpen && setShouldRenderMenu(true);
        if (!isMenuClicked && !isABTestEnabled) {
            // disable analytics in case of A/B test to avoid event duplication, since in this case the event will be triggered from AEM code
            setIsMenuClicked(true);
            dispatch(analyticsActions.mobileNavigationClick());
        }
    };
    const getLangItemLabel = (lang) => `${t(`${I18N_NAMESPACES.general}:language.${lang}`)} (${lang.toUpperCase()})`;
    const getEditLabel = (prefix) => (_jsxs(_Fragment, { children: [prefix, _jsx(Text, { color: "secondary", variant: "bodyM", fontWeight: "bold", children: t(`${I18N_NAMESPACES.navigation}:navigation.modify.label`) })] }));
    const getCurrencyItemLabel = (currency) => {
        return `${currency.name} (${currency.currency})`;
    };
    const languageItem = languages.length && languages.length > 1
        ? {
            id: 'language_level',
            title: t(`${I18N_NAMESPACES.navigation}:flyout.bottom.language`),
            spacing: 's',
            label: getEditLabel(getLangItemLabel(lang)),
            icon: IconName.GLOBE,
            type: 'common',
            subItems: languages
                .map(item => ({
                id: `lang_menu_item_${item}`,
                title: item,
                href: alternateUrl[item],
                label: getLangItemLabel(item),
                active: lang === item,
                type: 'common',
            }))
                .sort((lang1, lang2) => Number(lang2.active) - Number(lang1.active)),
        }
        : undefined;
    const currencyItem = (() => {
        if (!(currencies === null || currencies === void 0 ? void 0 : currencies.length) || currencies.length < 1)
            return undefined;
        const activeCurrency = currencies.find(item => item.active) || currencies[0];
        return {
            id: 'currency_level',
            title: t(`${I18N_NAMESPACES.navigation}:flyout.bottom.currency`),
            spacing: 's',
            label: getEditLabel(getCurrencyItemLabel(activeCurrency)),
            image: activeCurrency.flag,
            type: 'common',
            subItems: currencies
                .map(item => ({
                id: `currency_menu_item_${item.currency}`,
                title: item.currency,
                href: item.homepage,
                label: getCurrencyItemLabel(item),
                active: item.active,
                image: item.flag,
                type: 'common',
            }))
                .sort((currency1, currency2) => Number(currency2.active) - Number(currency1.active)),
        };
    })();
    if ((languageItem || currencyItem || accountConfig.enabled) && ((_a = menuItems === null || menuItems === void 0 ? void 0 : menuItems.subItems) === null || _a === void 0 ? void 0 : _a.length)) {
        menuItems.subItems.at(-1).spacing = 'l';
    }
    useLockBodyScroll(Boolean(isOpen));
    return (_jsxs(_Fragment, { children: [_jsx(MenuIcon, { testId: "mobile-navigation-trigger", isOpen: Boolean(isOpen), onClick: handleTriggerClick, theme: "branded" }), shouldRenderMenu &&
                createPortal(_jsx(Breakpoint, { until: "lg", children: _jsx(Box, { innerRef: menuWrapperRef, children: _jsx(Menu, { testId: "mobile-navigation", isOpen: isOpen, onClose: () => {
                                setIsOpen(false);
                                setShouldRenderMenu(false);
                            }, offsetTop: HEADER_HEIGHT, defaultLevel: defaultLevel, width: MENU_MAX_WIDTH, children: getMenuLevel({
                                item: menuItems,
                                backBtnLabel: menuItems.title,
                                isRootLevel: true,
                                isShopLanding,
                                searchConfig,
                                accountConfig,
                                currencyItem,
                                languageItem,
                            }) }) }) }), document.body)] }));
};
